import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { acquistiApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '../ui/select';
import { toast } from 'sonner';
import { Loader2, TrendingUp, AlertTriangle, Trophy, ChevronRight } from 'lucide-react';
import {
  ComposedChart, Bar, Line, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, ReferenceLine,
} from 'recharts';
import { fmtSoldi, fmtPct, fmtQta, ND } from '../../lib/acquisti';
import { GRANULARITA, aggrega, conAnnoPrima, pctSu } from '../../lib/trend';

const COLORE = { entrate: '#15803D', uscite: '#D94F3D', utile: '#1C1917', margine: '#D97706', prec: '#A8A29E' };

const METRICHE = [
  { key: 'conto_economico', label: 'Entrate, uscite e utile', tipo: 'ce' },
  { key: 'entrate', label: 'Entrate (venduto)', tipo: 'euro', netto: 'entrate_netto' },
  { key: 'uscite', label: 'Uscite totali', tipo: 'euro', netto: 'uscite_netto' },
  { key: 'utile', label: 'Utile / perdita', tipo: 'euro', netto: 'utile_netto', soloCompleti: true },
  { key: 'margine_pct', label: 'Margine % sul venduto', tipo: 'pct', netto: 'margine_netto_pct', soloCompleti: true },
  { key: 'acquisti', label: 'Acquisti da fatture', tipo: 'euro', netto: 'acquisti_netto' },
  { key: 'materia_prima', label: 'Materia prima (food & beverage)', tipo: 'euro', netto: 'materia_prima_netto' },
  { key: 'food_cost_pct', label: 'Food cost % sul venduto', tipo: 'pct' },
  { key: 'personale', label: 'Costo del personale', tipo: 'euro' },
  { key: 'n_documenti', label: 'Numero di fatture', tipo: 'num' },
];

const CAMPI_BASE = [
  'entrate', 'entrate_netto', 'uscite', 'uscite_netto', 'materia_prima', 'materia_prima_netto',
  'acquisti', 'acquisti_netto', 'personale', 'n_documenti', 'coperti', 'giornate',
];

const fmtAsse = (v) => (Math.abs(v) >= 1000 ? `${Math.round(v / 1000)}k` : `${v}`);

const Chip = ({ attivo, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
      attivo ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground'
    }`}
  >
    {children}
  </button>
);

const Legenda = ({ ce, metrica, annoPrima }) => (
  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
    {ce ? (
      <>
        {[['Entrate', COLORE.entrate], ['Uscite', COLORE.uscite], ['Utile', COLORE.utile], ['Margine %', COLORE.margine]].map(([label, colore]) => (
          <span key={label} className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: colore }} />
            {label}
          </span>
        ))}
        <span>Barra sbiadita = periodo con uscite incomplete</span>
      </>
    ) : (
      <>
        <span className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: COLORE.entrate }} />
          {metrica.label}
        </span>
        {annoPrima && (
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: COLORE.prec }} />
            Stesso periodo dell'anno prima
          </span>
        )}
      </>
    )}
  </div>
);

const valoreFmt = (tipo, v) => {
  if (v === null || v === undefined) return ND;
  if (tipo === 'pct') return fmtPct(v);
  if (tipo === 'num') return fmtQta(v);
  return fmtSoldi(v);
};

const TooltipMese = ({ active, payload, metrica }) => {
  if (!active || !payload?.length) return null;
  const g = payload[0].payload;
  return (
    <div className="rounded-lg border bg-background/95 p-2.5 text-xs shadow-lg space-y-1 max-w-[230px]">
      <p className="font-bold capitalize">{g.label}</p>
      {metrica.tipo === 'ce' ? (
        <>
          <p className="flex justify-between gap-4">
            <span className="text-muted-foreground">Entrate</span>
            <span className="font-semibold tabular-nums">{fmtSoldi(g.e)}</span>
          </p>
          <p className="flex justify-between gap-4">
            <span className="text-muted-foreground">Uscite</span>
            <span className="font-semibold tabular-nums">{fmtSoldi(g.u)}</span>
          </p>
          {g.costi_completi ? (
            <>
              <p className="flex justify-between gap-4 border-t pt-1">
                <span className="text-muted-foreground">{g.ut >= 0 ? 'Utile' : 'Perdita'}</span>
                <span className={`font-bold tabular-nums ${g.ut >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                  {fmtSoldi(g.ut)}
                </span>
              </p>
              <p className="flex justify-between gap-4">
                <span className="text-muted-foreground">Margine</span>
                <span className="font-semibold tabular-nums">{fmtPct(g.mg)}</span>
              </p>
              {g.food_cost_pct !== null && (
                <p className="text-[11px] text-muted-foreground">
                  food {fmtPct(g.food_cost_pct)} · personale {fmtPct(g.personale_pct)}
                </p>
              )}
            </>
          ) : (
            <p className="text-[11px] text-amber-700 border-t pt-1">
              Uscite incomplete ({g.fonti_mancanti.join(', ')}): utile e margine non calcolabili.
            </p>
          )}
        </>
      ) : (
        <>
          <p className="flex justify-between gap-4">
            <span className="text-muted-foreground">{metrica.label}</span>
            <span className="font-bold tabular-nums">{valoreFmt(metrica.tipo, g.v)}</span>
          </p>
          {g.vp !== null && g.vp !== undefined && (
            <p className="text-[11px] text-muted-foreground">
              {g.label_prec}: {valoreFmt(metrica.tipo, g.vp)}
              {g.delta_pct !== null && ` · ${g.delta_pct > 0 ? '+' : ''}${String(g.delta_pct).replace('.', ',')}%`}
            </p>
          )}
          <p className="text-[11px] text-muted-foreground border-t pt-1">
            entrate {fmtSoldi(g.entrate)} · {g.n_mesi} {g.n_mesi === 1 ? 'mese' : 'mesi'}
          </p>
        </>
      )}
    </div>
  );
};

const RigaTotale = ({ label, valore, forte, colore }) => (
  <p className="flex items-baseline justify-between gap-3">
    <span className="text-muted-foreground">{label}</span>
    <span className={`tabular-nums ${forte ? 'font-bold' : 'font-semibold'} ${colore || ''}`}>{valore}</span>
  </p>
);

export const AndamentoMensileCard = ({ className = '' }) => {
  const navigate = useNavigate();
  const [serie, setSerie] = useState(null);
  const [caricando, setCaricando] = useState(true);
  const [netto, setNetto] = useState(false);
  const [finestra, setFinestra] = useState('tutto');
  const [metricaKey, setMetricaKey] = useState('conto_economico');
  const [gran, setGran] = useState('mese');
  const [annoPrima, setAnnoPrima] = useState(false);

  useEffect(() => {
    acquistiApi.getAndamentoMensile()
      .then(setSerie)
      .catch((e) => toast.error(e.response?.data?.detail || 'Andamento mensile non disponibile'))
      .finally(() => setCaricando(false));
  }, []);

  const anni = useMemo(
    () => [...new Set((serie?.mesi || []).map((m) => m.mese.slice(0, 4)))].sort().reverse(),
    [serie],
  );

  const categorie = useMemo(() => {
    const viste = new Map();
    (serie?.mesi || []).forEach((m) => (m.categorie || []).forEach((c) => {
      viste.set(c.categoria, (viste.get(c.categoria) || 0) + (c.totale || 0));
    }));
    return [...viste.entries()].sort((a, b) => b[1] - a[1]).map(([categoria]) => categoria);
  }, [serie]);

  const metrica = useMemo(() => {
    if (metricaKey.startsWith('cat:')) {
      const categoria = metricaKey.slice(4);
      return {
        key: `catv:${categoria}`,
        netto: `catn:${categoria}`,
        label: `Acquisti: ${categoria}`,
        tipo: 'euro',
      };
    }
    return METRICHE.find((m) => m.key === metricaKey) || METRICHE[0];
  }, [metricaKey]);

  const righe = useMemo(() => {
    let mesi = serie?.mesi || [];
    if (finestra === '12') mesi = mesi.slice(-12);
    else if (finestra !== 'tutto') mesi = mesi.filter((m) => m.mese.startsWith(finestra));
    return mesi.map((m) => {
      const piatta = { ...m };
      (m.categorie || []).forEach((c) => {
        piatta[`catv:${c.categoria}`] = c.totale;
        piatta[`catn:${c.categoria}`] = c.totale_netto;
      });
      return piatta;
    });
  }, [serie, finestra]);

  const campiSomma = useMemo(
    () => [...CAMPI_BASE, ...categorie.flatMap((c) => [`catv:${c}`, `catn:${c}`])],
    [categorie],
  );

  const chiaveMetrica = netto && metrica.netto ? metrica.netto : metrica.key;

  const gruppi = useMemo(() => {
    const base = aggrega(righe, gran, campiSomma, (g) => {
      const completi = g.righe.every((r) => r.costi_completi);
      const utile = Number((g.entrate - g.uscite).toFixed(2));
      const utileNetto = Number((g.entrate_netto - g.uscite_netto).toFixed(2));
      return {
        costi_completi: completi,
        fonti_mancanti: [...new Set(g.righe.flatMap((r) => r.fonti_mancanti))],
        n_incompleti: g.righe.filter((r) => !r.costi_completi).length,
        venduto_disponibile: g.entrate > 0,
        utile: completi ? utile : null,
        utile_netto: completi ? utileNetto : null,
        margine_pct: completi ? pctSu(utile, g.entrate) : null,
        margine_netto_pct: completi ? pctSu(utileNetto, g.entrate_netto) : null,
        food_cost_pct: g.materia_prima ? pctSu(g.materia_prima, g.entrate) : null,
        personale_pct: g.personale ? pctSu(g.personale, g.entrate) : null,
      };
    });
    return conAnnoPrima(base, gran, chiaveMetrica);
  }, [righe, gran, campiSomma, chiaveMetrica]);

  const dati = useMemo(() => gruppi.map((g) => ({
    ...g,
    e: netto ? g.entrate_netto : g.entrate,
    u: netto ? g.uscite_netto : g.uscite,
    ut: netto ? g.utile_netto : g.utile,
    mg: netto ? g.margine_netto_pct : g.margine_pct,
    v: metrica.soloCompleti && !g.costi_completi ? null : g.valore,
    vp: annoPrima ? g.valore_prec : null,
  })), [gruppi, netto, metrica.soloCompleti, annoPrima]);

  if (caricando) {
    return (
      <Card className={className} data-testid="andamento-mensile-caricamento">
        <CardContent className="p-4 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Calcolo dell'andamento mensile…
        </CardContent>
      </Card>
    );
  }
  if (!serie?.mesi?.length) return null;

  const completi = gruppi.filter((g) => g.costi_completi && g.venduto_disponibile);
  const incompleti = gruppi.length - completi.length;
  const agg = (chiave) => Number(completi.reduce((s, g) => s + (g[chiave] || 0), 0).toFixed(2));
  const entrateC = netto ? agg('entrate_netto') : agg('entrate');
  const usciteC = netto ? agg('uscite_netto') : agg('uscite');
  const utileC = Number((entrateC - usciteC).toFixed(2));
  const ordinati = [...completi].sort((a, b) => (a.utile || 0) - (b.utile || 0));
  const migliore = ordinati.length ? ordinati[ordinati.length - 1] : null;
  const peggiore = ordinati.length > 1 ? ordinati[0] : null;
  const ce = metrica.tipo === 'ce';

  return (
    <Card className={className} data-testid="andamento-mensile">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-primary" /> Confronto mensile: entrate, uscite e utile
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex gap-1.5 flex-wrap" data-testid="andamento-finestra">
            {[['tutto', 'Tutto'], ['12', 'Ultimi 12 mesi'], ...anni.map((a) => [a, a])].map(([k, label]) => (
              <Chip key={k} attivo={finestra === k} onClick={() => setFinestra(k)} testid={`andamento-finestra-${k}`}>
                {label}
              </Chip>
            ))}
          </div>
          <div className="flex gap-1.5 ml-auto" data-testid="andamento-iva">
            {[[false, 'IVA inclusa'], [true, 'Netto IVA']].map(([v, label]) => (
              <button
                key={label}
                onClick={() => setNetto(v)}
                data-testid={`andamento-iva-${v ? 'netto' : 'lordo'}`}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                  netto === v ? 'bg-foreground text-background' : 'bg-secondary text-muted-foreground'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Select value={metricaKey} onValueChange={setMetricaKey}>
            <SelectTrigger className="h-9 text-xs w-full sm:w-[300px]" data-testid="andamento-metrica">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {METRICHE.map((m) => (
                <SelectItem key={m.key} value={m.key} className="text-xs" data-testid={`andamento-metrica-${m.key}`}>
                  {m.label}
                </SelectItem>
              ))}
              {categorie.length > 0 && (
                <SelectGroup>
                  <SelectLabel className="text-[10px] uppercase tracking-wide">Acquisti per categoria</SelectLabel>
                  {categorie.map((c) => (
                    <SelectItem key={c} value={`cat:${c}`} className="text-xs" data-testid={`andamento-metrica-cat-${c}`}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectGroup>
              )}
            </SelectContent>
          </Select>
          <div className="flex gap-1.5" data-testid="andamento-granularita">
            {GRANULARITA.map((g) => (
              <Chip key={g.key} attivo={gran === g.key} onClick={() => setGran(g.key)} testid={`andamento-gran-${g.key}`}>
                {g.label}
              </Chip>
            ))}
          </div>
          {!ce && (
            <Chip attivo={annoPrima} onClick={() => setAnnoPrima(!annoPrima)} testid="andamento-anno-prima">
              vs anno prima
            </Chip>
          )}
        </div>

        <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
          <div>
            <div className="h-64 lg:h-80 -ml-2" data-testid="andamento-grafico">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={dati} margin={{ top: 8, right: 4, left: -8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E7E5E4" />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                  <YAxis
                    yAxisId="soldi"
                    tick={{ fontSize: 10 }}
                    width={44}
                    tickFormatter={(v) => (metrica.tipo === 'pct' ? `${Math.round(v)}%` : fmtAsse(v))}
                  />
                  {ce && (
                    <YAxis
                      yAxisId="pct"
                      orientation="right"
                      tick={{ fontSize: 10 }}
                      tickFormatter={(v) => `${Math.round(v)}%`}
                      width={38}
                    />
                  )}
                  <Tooltip content={<TooltipMese metrica={metrica} />} />
                  <ReferenceLine yAxisId="soldi" y={0} stroke="#A8A29E" />
                  {ce ? (
                    <>
                      <Bar yAxisId="soldi" dataKey="e" name="Entrate" fill={COLORE.entrate} radius={[3, 3, 0, 0]}>
                        {dati.map((g) => <Cell key={g.chiave} fillOpacity={g.venduto_disponibile ? 0.9 : 0.3} />)}
                      </Bar>
                      <Bar yAxisId="soldi" dataKey="u" name="Uscite" fill={COLORE.uscite} radius={[3, 3, 0, 0]}>
                        {dati.map((g) => <Cell key={g.chiave} fillOpacity={g.costi_completi ? 0.9 : 0.3} />)}
                      </Bar>
                      <Line
                        yAxisId="soldi" type="monotone" dataKey="ut" name="Utile"
                        stroke={COLORE.utile} strokeWidth={2.5} dot={{ r: 3 }} connectNulls={false}
                      />
                      <Line
                        yAxisId="pct" type="monotone" dataKey="mg" name="Margine %"
                        stroke={COLORE.margine} strokeWidth={2} strokeDasharray="4 3" dot={{ r: 2 }} connectNulls={false}
                      />
                    </>
                  ) : (
                    <>
                      {annoPrima && (
                        <Bar yAxisId="soldi" dataKey="vp" name="Anno prima" fill={COLORE.prec} radius={[3, 3, 0, 0]} />
                      )}
                      <Bar yAxisId="soldi" dataKey="v" name={metrica.label} fill={COLORE.entrate} radius={[3, 3, 0, 0]} />
                    </>
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <Legenda ce={ce} metrica={metrica} annoPrima={annoPrima} />
          </div>

          <div className="space-y-3 text-sm">
            <div className="rounded-lg border p-3 space-y-1" data-testid="andamento-totali-completi">
              <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Periodi con uscite complete: {completi.length} di {gruppi.length}
              </p>
              {completi.length ? (
                <>
                  <RigaTotale label="Entrate" valore={fmtSoldi(entrateC)} />
                  <RigaTotale label="Uscite" valore={fmtSoldi(usciteC)} />
                  <RigaTotale
                    label={utileC >= 0 ? 'Utile' : 'Perdita'}
                    valore={fmtSoldi(utileC)}
                    forte
                    colore={utileC >= 0 ? 'text-emerald-700' : 'text-red-700'}
                  />
                  <RigaTotale label="Margine" valore={fmtPct(pctSu(utileC, entrateC))} />
                </>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Nessun periodo completo nella finestra scelta: l'utile si calcola solo dove ci sono
                  tutte le fonti di costo.
                </p>
              )}
            </div>

            {migliore && (
              <div className="rounded-lg border p-3 space-y-1.5" data-testid="andamento-migliore-peggiore">
                <p className="flex items-baseline justify-between gap-2">
                  <span className="text-muted-foreground flex items-center gap-1.5">
                    <Trophy className="w-3.5 h-3.5 text-emerald-600" /> Periodo migliore
                  </span>
                  <span className="font-semibold capitalize">{migliore.label}</span>
                </p>
                <p className="text-[11px] text-muted-foreground">
                  {fmtSoldi(migliore.utile)} di utile · margine {fmtPct(migliore.margine_pct)}
                </p>
                {peggiore && (
                  <>
                    <p className="flex items-baseline justify-between gap-2 pt-1.5 border-t">
                      <span className="text-muted-foreground">Periodo peggiore</span>
                      <span className="font-semibold capitalize">{peggiore.label}</span>
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      {fmtSoldi(peggiore.utile)} di utile · margine {fmtPct(peggiore.margine_pct)}
                    </p>
                  </>
                )}
              </div>
            )}

            {incompleti > 0 && (
              <div
                className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-3 text-[11px] text-amber-800 space-y-1.5"
                data-testid="andamento-avviso-incompleti"
              >
                <p className="font-semibold flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" /> {incompleti} periodi con uscite incomplete
                </p>
                <p>
                  Per quei periodi mancano fatture, costo del personale o costi bancari: utile e
                  margine restano in bianco perché non sarebbero confrontabili.
                </p>
                <button
                  type="button"
                  className="font-semibold underline flex items-center gap-1"
                  onClick={() => navigate('/promemoria')}
                  data-testid="andamento-vai-promemoria"
                >
                  Completa i caricamenti <ChevronRight className="w-3 h-3" />
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="overflow-x-auto -mx-4 px-4" data-testid="andamento-tabella">
          <table className="w-full min-w-[680px] text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wide text-muted-foreground border-b">
                <th className="text-left font-medium py-1.5">Periodo</th>
                <th className="text-right font-medium py-1.5">Entrate</th>
                <th className="text-right font-medium py-1.5">Uscite</th>
                <th className="text-right font-medium py-1.5">Utile</th>
                <th className="text-right font-medium py-1.5">Margine</th>
                <th className="text-right font-medium py-1.5">Acquisti</th>
                <th className="text-right font-medium py-1.5">Food</th>
                <th className="text-right font-medium py-1.5">Personale</th>
              </tr>
            </thead>
            <tbody>
              {[...dati].reverse().map((g) => (
                <tr key={g.chiave} className="border-b last:border-0" data-testid={`andamento-riga-${g.chiave}`}>
                  <td className="py-1.5 capitalize whitespace-nowrap">
                    {g.label}
                    {!g.costi_completi && (
                      <Badge variant="outline" className="ml-1.5 text-[9px] border-amber-500 text-amber-700">
                        parziale
                      </Badge>
                    )}
                  </td>
                  <td className="py-1.5 text-right tabular-nums">{fmtSoldi(g.e)}</td>
                  <td className="py-1.5 text-right tabular-nums">{fmtSoldi(g.u)}</td>
                  <td className={`py-1.5 text-right tabular-nums font-semibold ${
                    g.ut === null ? 'text-muted-foreground' : g.ut >= 0 ? 'text-emerald-700' : 'text-red-700'
                  }`}>
                    {g.ut === null ? '—' : fmtSoldi(g.ut)}
                  </td>
                  <td className="py-1.5 text-right tabular-nums">{g.mg === null ? '—' : fmtPct(g.mg)}</td>
                  <td className="py-1.5 text-right tabular-nums text-muted-foreground">
                    {fmtSoldi(netto ? g.acquisti_netto : g.acquisti)}
                  </td>
                  <td className="py-1.5 text-right tabular-nums text-muted-foreground">
                    {g.food_cost_pct === null ? '—' : fmtPct(g.food_cost_pct)}
                  </td>
                  <td className="py-1.5 text-right tabular-nums text-muted-foreground">
                    {g.personale_pct === null ? '—' : fmtPct(g.personale_pct)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default AndamentoMensileCard;
